import React from 'react';

/**
 * Site → Header & footer, read-only. The global header and footer live in the
 * site document beside the theme; the agent edits them (update_site) and they
 * publish with the theme. This shows the working version so a member can see
 * what the next theme publish will put live.
 */

interface Link {
  label: string;
  href?: string | null;
  children?: Link[];
}

interface Navigation {
  header?: Link[];
  cta?: Link | null;
  footer?: Link[];
  footerColumns?: Array<{ heading?: string | null; links: Link[] }>;
  footerText?: string | null;
  social?: Array<{ network: string; href: string }>;
}

const Row: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="ink-site-row" style={{ alignItems: 'start' }}>
    <div style={{ minWidth: 0 }}>
      <div className="ink-site-row-title">{title}</div>
      <div className="ink-site-row-desc">{children}</div>
    </div>
  </div>
);

const linkText = (l: Link) => (l.href ? `${l.label} → ${l.href}` : l.label);

export function CmsNavigation({
  navigation,
  homePage,
  showTagline,
}: {
  navigation?: Navigation | null;
  homePage?: string | null;
  showTagline?: boolean;
}) {
  const nav = navigation || {};
  const header = nav.header || [];
  const columns = nav.footerColumns || [];
  const footer = nav.footer || [];
  const social = nav.social || [];
  const none = <span style={{ color: 'var(--ink-ink3)' }}>None</span>;

  return (
    <div className="ink-site-card">
      <Row title="Home page">{homePage ? `/${homePage}` : 'The article list'}</Row>
      <Row title="Header links">
        {header.length
          ? header.map((l, i) => (
              <span key={i} style={{ display: 'block' }}>
                {linkText(l)}
                {l.children?.length ? (
                  <span style={{ display: 'block', paddingLeft: 14 }}>
                    {l.children.map((c, j) => (
                      <span key={j} style={{ display: 'block' }}>
                        ↳ {linkText(c)}
                      </span>
                    ))}
                  </span>
                ) : null}
              </span>
            ))
          : none}
      </Row>
      <Row title="Button">{nav.cta ? linkText(nav.cta) : none}</Row>
      <Row title="Tagline in header">{showTagline === false ? 'Hidden' : 'Shown'}</Row>
      <Row title="Footer">
        {columns.map((c, i) => (
          <span key={i} style={{ display: 'block', marginBottom: 6 }}>
            <strong style={{ fontWeight: 500 }}>{c.heading || 'Links'}:</strong>{' '}
            {c.links.map((l) => l.label).join(' · ')}
          </span>
        ))}
        {footer.length ? <span style={{ display: 'block' }}>{footer.map((l) => l.label).join(' · ')}</span> : null}
        {social.length ? (
          <span style={{ display: 'block' }}>Social: {social.map((s) => s.network).join(', ')}</span>
        ) : null}
        {nav.footerText ? <span style={{ display: 'block' }}>“{nav.footerText}”</span> : null}
        {!columns.length && !footer.length && !social.length && !nav.footerText ? none : null}
      </Row>
      <Row title="Changing these">
        Ask your agent — for example “add Pricing to the header” or “put our GitHub in the footer”. The
        changes go live with the next theme publish.
      </Row>
    </div>
  );
}
